import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlags } from '@atlaskit/flag';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { R400 } from '@atlaskit/theme/colors';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { useFormSaveErrorsActions } from '@atlassian/proforma-common-core/form-system';
import { convertAnswers } from '@atlassian/proforma-common-core/form-system-utils';
import { getFormSaveErrors, isUnknownApiError } from '@atlassian/proforma-common-core/jira-common-models';
import { Action, ActionSubject, FormUpdateType } from '@atlassian/proforma-utils/analytics';
import { getSimpleFormAnalytics } from '../../../utils/simple-form-analytics';
import { useFormIndexActions, useSelectedFormStoreState } from '../store';
import { messages } from './messages';
export const useHandleSaveForm = () => {
  const [isSaving, setIsSaving] = useState(false);
  const {
    selectedFormStore: formStore
  } = useSelectedFormStoreState();
  const {
    saveAnswers,
    reportError
  } = useFormIndexActions();
  const {
    clear: clearFormSaveErrors,
    set: setFormSaveErrors
  } = useFormSaveErrorsActions();
  const {
    showFlag
  } = useFlags();
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  return {
    isSaving,
    handleSaveForm: useCallback(async () => {
      setIsSaving(true);
      try {
        if (formStore && formStore.formId) {
          const answers = convertAnswers(formStore.visibleQuestions);
          const savedForm = await saveAnswers(formStore.formId, answers);
          clearFormSaveErrors();
          await formStore.update(savedForm);
          fireTrackAnalytics(createAnalyticsEvent({}), `${ActionSubject.FORM} ${Action.UPDATED}`, {
            formUpdateType: FormUpdateType.SAVE,
            ...getSimpleFormAnalytics(formStore.formId, formStore.templateFormId, formStore.status, formStore.internal)
          });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error) {
        const formSaveErrors = getFormSaveErrors(error);
        if (formSaveErrors) {
          setFormSaveErrors(formSaveErrors.errorsByQuestionId);
          const showGeneralErrorMsg = Object.keys(formSaveErrors.errorsByQuestionId).length === 0 && formSaveErrors.generalErrors.length > 0;
          const customDescription = showGeneralErrorMsg ? formSaveErrors === null || formSaveErrors === void 0 ? void 0 : formSaveErrors.generalErrors[0] : undefined;
          showFlag({
            icon: /*#__PURE__*/React.createElement(ErrorIcon, {
              spacing: "spacious",
              label: "",
              color: `var(--ds-icon-danger, ${R400})`
            }),
            title: /*#__PURE__*/React.createElement(FormattedMessage, messages.errorSaveFormTitle),
            description: customDescription || /*#__PURE__*/React.createElement(FormattedMessage, messages.errorSaveFormInvalidAnswersDescription),
            isAutoDismiss: true
          });
          throw error;
        }
        let description;
        if (isUnknownApiError(error) && error.status === 409) {
          description = /*#__PURE__*/React.createElement(FormattedMessage, messages.errorSaveFormFailedOnConflictDescription);
        } else {
          reportError(error, {
            id: 'saveForm',
            attributes: {
              formId: formStore === null || formStore === void 0 ? void 0 : formStore.formId
            }
          });
          description = /*#__PURE__*/React.createElement(FormattedMessage, messages.errorSaveFormServerErrorDescription);
        }
        showFlag({
          icon: /*#__PURE__*/React.createElement(ErrorIcon, {
            spacing: "spacious",
            label: "",
            color: `var(--ds-icon-danger, ${R400})`
          }),
          title: /*#__PURE__*/React.createElement(FormattedMessage, messages.errorSaveFormTitle),
          description,
          isAutoDismiss: true
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    }, [formStore, saveAnswers, clearFormSaveErrors, reportError, showFlag, setFormSaveErrors, createAnalyticsEvent])
  };
};